import React from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import ReactGA4 from 'react-ga4';
import coinLogo from '../assets/COINLogo.png';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { isQuickFlipSearchEnabled } from '../config/featureFlags';
import './SharedHeader.css';

const trackCta = (action, destination) => {
  ReactGA4.event('player_market_cta', { action, destination });
};

// Routes that require auth. Logging out while on one of these has to send
// you somewhere else; logging out from a public page (Home, Quick Flip)
// should leave you right where you were.
const PROTECTED_PATHS = ['/dashboard', '/subscribe', '/faq', '/admin', '/settings/ai'];

function SharedHeader({ presentationMode, variant = 'default', onCreateAccount, onBeforeLogin }) {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { profitPlannerEnabled, coinMcpEnabled, gradingPlusCheckoutEnabled } = useFeatureFlags();
  const showAiSettings = (coinMcpEnabled || gradingPlusCheckoutEnabled) && !presentationMode;
  const quickFlipEnabled = isQuickFlipSearchEnabled();
  const effectiveAuthenticated = presentationMode
    ? presentationMode === 'subscriber'
    : isAuthenticated;
  const effectiveSubscriber = presentationMode
    ? presentationMode === 'subscriber'
    : Boolean(user?.is_subscribed);
  const isQuickFlip = variant === 'quick-flip';
  // A free-tier account never had a subscription, so it gets an upgrade CTA
  // rather than the reactivation CTA a lapsed subscriber sees.
  const isFreeTier = !presentationMode && (user?.sub_tier || 'free') === 'free';
  // Never shown in a reviewer preview, which has no real account behind it.
  const isAdmin = !presentationMode && Boolean(user?.is_admin);

  const handleLogout = async () => {
    await logout();
    // Only bounce to /login when the page you were on requires auth. From a
    // public page (Home, Quick Flip) staying put is more seamless.
    if (PROTECTED_PATHS.includes(location.pathname)) {
      navigate('/login');
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await fetch('/api/payment/create-portal-session', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.url) window.location.href = data.url;
    } catch (error) {
      console.error('Error accessing billing portal:', error);
    }
  };

  const handleReactivate = async () => {
    trackCta('reactivate', '/subscribe');
    try {
      const response = await fetch('/api/payment/create-reactivation-session', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.sessionId && window.Stripe) {
        const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51QGJgWP7BpKe4bdjJB6wZUTHMzVojLSA1GMBWI1KkHJWyGhA3CfYHcTcCFOyBaL5dTyQwt8KNCWJ9OdcL2cL8Jir00zY8zKy7A');
        await stripe.redirectToCheckout({ sessionId: data.sessionId });
        return;
      }
    } catch (error) {
      console.error('Error starting reactivation:', error);
    }
    navigate('/subscribe');
  };

  return (
    <header className={`site-header${isQuickFlip ? ' site-header--quick-flip' : ''}`}>
      <div className="site-header__inner">
        <Link className="site-header__brand" to="/" aria-label="COIN home">
          <img src={coinLogo} alt="COIN" />
        </Link>
        <nav className="site-nav" aria-label="Primary navigation">
          <NavLink to="/" end>Home</NavLink>
          {!effectiveAuthenticated && (
            <>
              <NavLink to="/grading">Grading</NavLink>
              {quickFlipEnabled && !isQuickFlip && <NavLink to="/search">COIN Search</NavLink>}
              {profitPlannerEnabled && <NavLink to="/profit-planner">Profit Planner</NavLink>}
              <NavLink to={isQuickFlip ? '/login?redirect=/search' : '/login'} onClick={() => {
                onBeforeLogin?.();
                trackCta('login', '/login');
              }}>Log In</NavLink>
              {isQuickFlip && onCreateAccount ? (
                <button className="site-nav__cta" type="button"
                  onClick={() => { trackCta('create_account', '/search'); onCreateAccount(); }}>
                  Create Account
                </button>
              ) : (
                <NavLink className="site-nav__cta" to="/register" onClick={() => trackCta('trial', '/register')}>Start Free Trial</NavLink>
              )}
            </>
          )}
          {effectiveAuthenticated && effectiveSubscriber && (
            <>
              <NavLink className="site-nav__dashboard" to="/dashboard">Grading</NavLink>
              {quickFlipEnabled && !isQuickFlip && <NavLink to="/search">COIN Search</NavLink>}
              {profitPlannerEnabled && <NavLink to="/profit-planner">Profit Planner</NavLink>}
              {showAiSettings && <NavLink to="/settings/ai">AI Connections</NavLink>}
              {isAdmin && <NavLink to="/admin">Admin</NavLink>}
              {!isQuickFlip && (presentationMode ? (
                <button type="button" disabled title="Reviewer preview">Manage Subscription</button>
              ) : (
                <button type="button" onClick={handleManageSubscription}>Manage Subscription</button>
              ))}
              {presentationMode ? (
                <button className="site-nav__logout" type="button" disabled title="Reviewer preview">Logout</button>
              ) : (
                <button className="site-nav__logout" type="button" onClick={handleLogout}>Logout</button>
              )}
            </>
          )}
          {effectiveAuthenticated && !effectiveSubscriber && (
            <>
              <NavLink to="/dashboard">Grading</NavLink>
              {quickFlipEnabled && !isQuickFlip && <NavLink to="/search">Search</NavLink>}
              {profitPlannerEnabled && <NavLink to="/profit-planner">Profit Planner</NavLink>}
              {showAiSettings && <NavLink to="/settings/ai">AI Connections</NavLink>}
              {isAdmin && <NavLink to="/admin">Admin</NavLink>}
              {isFreeTier ? (
                <NavLink className="site-nav__cta" to="/register"
                  onClick={() => trackCta('upgrade', '/register')}>{isQuickFlip ? 'Upgrade' : 'Upgrade Sub'}</NavLink>
              ) : (
                <button className="site-nav__cta" type="button" onClick={handleReactivate}>Reactivate</button>
              )}
              <button className="site-nav__logout" type="button" onClick={handleLogout}>Logout</button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

export default SharedHeader;
